/**
 * InviteToAppButton — Shows client app invite status and provides
 * invite/revoke actions for the therapist dashboard.
 */
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthProvider";
import { apiPost } from "../lib/api-client";
import { useToast } from "./Toast";

type Props = {
  patientId: string;
  email: string;
  clientStatus?: string; // "invited" | "active" | "disabled" | undefined
  onStatusChange?: (status: string) => void;
};

const InviteToAppButton: React.FC<Props> = ({
  patientId,
  email,
  clientStatus,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { ensureToken } = useAuth();
  const [loading, setLoading] = useState(false);

  const getToken = useCallback(async () => {
    const tk = await ensureToken();
    if (!tk) throw new Error("No auth token");
    return tk;
  }, [ensureToken]);

  const handleInvite = useCallback(async () => {
    if (!email) {
      toast.error(t("patients.noEmail"));
      return;
    }
    setLoading(true);
    try {
      const r = await apiPost<{ ok: boolean; invite_sent_to: string; client_status: string }>(
        `/patients/${patientId}/invite`,
        {},
        getToken
      );
      toast.info(t("patients.inviteSuccess", { email: r.invite_sent_to }));
      onStatusChange?.(r.client_status);
    } catch (e) {
      toast.error(
        `${t("patients.inviteError")}: ${e instanceof Error ? e.message : String(e)}`
      );
    } finally {
      setLoading(false);
    }
  }, [patientId, email, getToken, toast, t, onStatusChange]);

  const handleRevoke = useCallback(async () => {
    setLoading(true);
    try {
      await apiPost<{ ok: boolean }>(
        `/patients/${patientId}/revoke`,
        {},
        getToken
      );
      toast.info(t("patients.revokeSuccess"));
      onStatusChange?.("disabled");
    } catch (e) {
      toast.error(
        `${t("patients.revokeError")}: ${e instanceof Error ? e.message : String(e)}`
      );
    } finally {
      setLoading(false);
    }
  }, [patientId, getToken, toast, t, onStatusChange]);

  const btnBase: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "5px 10px",
    border: "1px solid var(--color-border)",
    borderRadius: 8,
    fontSize: 12,
    cursor: loading ? "wait" : "pointer",
    background: "var(--color-surface)",
  };

  if (clientStatus === "active" || clientStatus === "invited") {
    const isActive = clientStatus === "active";
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 6 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: "50%",
            background: isActive ? "var(--color-success)" : "var(--color-warning)",
            display: "inline-block",
          }}
        />
        <span
          style={{
            fontSize: 12,
            color: isActive ? "var(--color-success)" : "var(--color-warning)",
            fontWeight: 600,
          }}
        >
          {isActive ? t("patients.inviteActive") : t("patients.inviteSent")}
        </span>
        <button
          style={{ ...btnBase, color: "var(--color-danger)", borderColor: "var(--color-danger-border)" }}
          onClick={handleRevoke}
          disabled={loading}
        >
          {t("patients.revokeAccess")}
        </button>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6 }}>
      <button style={btnBase} onClick={handleInvite} disabled={loading}>
        {loading ? t("patients.inviteSending") : t("patients.inviteToApp")}
      </button>
    </div>
  );
};

export default InviteToAppButton;
